import { Discount } from '@dataTypes/discount.type'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_BILLING', '')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type CreateDiscountRequest = {
  status: SettingStatus
  description: string
  discount_percentage: number
  clinic_id: string
}

export type UpdateDiscountRequest = {
  discount_id: string
  status?: SettingStatus
  description?: string
  discount_percentage?: number
  clinic_id?: string
}

export type DiscountQueryFilters = {
  clinic_id?: string
  status?: SettingStatus | SettingStatus[]
  search?: string
  sort?: string
  limit?: number
  offset?: number
}

export type DiscountListResponse = {
  discounts: Discount[]
  count: number
}

export const getAllDiscounts = async (
  filters?: DiscountQueryFilters,
): Promise<DiscountListResponse> => {
  try {
    const response = await axios.get<
      ApiResponse<{ discounts?: Discount[]; count?: number }>
    >(`${BUSINESS_LAYER_API}/api/v1/discount`, {
      params: filters,
    })

    return {
      discounts: response.data?.data?.discounts || [],
      count: response.data?.data?.count || 0,
    }
  } catch (e: unknown) {
    logger.error('Error in getAllDiscounts:', e)
    throw e
  }
}

export const addDiscount = async (
  payload: CreateDiscountRequest,
): Promise<Discount> => {
  try {
    const response = await axios.post<ApiResponse<{ discount?: Discount }>>(
      `${BUSINESS_LAYER_API}/api/v1/discount`,
      payload,
    )

    if (response.data?.data?.discount) {
      return response.data.data.discount
    }
    throw new Error('Failed to create discount')
  } catch (e: unknown) {
    logger.error('Error in addDiscount:', e)
    throw e
  }
}

export const updateDiscount = async (
  payload: UpdateDiscountRequest,
): Promise<Discount> => {
  try {
    const response = await axios.put<ApiResponse<{ discount?: Discount }>>(
      `${BUSINESS_LAYER_API}/api/v1/discount`,
      payload,
    )

    if (response.data?.data?.discount) {
      return response.data.data.discount
    }
    throw new Error('Failed to update discount')
  } catch (e: unknown) {
    logger.error('Error in updateDiscount:', e)
    throw e
  }
}

export const deleteDiscount = async (id: string): Promise<void> => {
  try {
    await axios.delete<ApiResponse<Record<string, never>>>(
      `${BUSINESS_LAYER_API}/api/v1/discount/${id}`,
    )
  } catch (e: unknown) {
    logger.error('Error in deleteDiscount:', e)
    throw e
  }
}
